// Builds the Mantine theme from the brand palettes. Contrast is validated at
// build time (plugins/brand.ts), so the colours used here are WCAG AA (NFR-07).
import {
  createTheme,
  defaultVariantColorsResolver,
  virtualColor,
  type CSSVariablesResolver,
  type MantineColorsTuple,
  type VariantColorsResolver,
} from "@mantine/core";
import type { PublicBrand } from "./brand";

function hexToRgb(hex: string): [number, number, number] {
  const v = parseInt(hex.slice(1), 16);
  return [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff];
}

function mix(hex: string, with_: string, amount: number): string {
  const a = hexToRgb(hex);
  const b = hexToRgb(with_);
  const c = a.map((x, i) => Math.round(x + ((b[i] ?? x) - x) * amount));
  return (
    "#" +
    c
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase()
  );
}

/** shades returns ten shades with the brand colour itself at index 6. */
export function shades(hex: string): MantineColorsTuple {
  const light = [0.9, 0.75, 0.6, 0.45, 0.3, 0.15].map((a) => mix(hex, "#FFFFFF", a));
  const dark = [0.15, 0.3, 0.45].map((a) => mix(hex, "#000000", a));
  return [...light, hex.toUpperCase(), ...dark] as unknown as MantineColorsTuple;
}

export function buildTheme(brand: PublicBrand) {
  return createTheme({
    fontFamily: brand.theme.font_family,
    headings: { fontFamily: brand.theme.font_family },
    defaultRadius: brand.theme.radius as "xs" | "sm" | "md" | "lg" | "xl",
    colors: {
      "brand-light": shades(brand.theme.light.primary),
      "brand-dark": shades(brand.theme.dark.primary),
      brand: virtualColor({ name: "brand", light: "brand-light", dark: "brand-dark" }),
    },
    primaryColor: "brand",
    primaryShade: { light: 6, dark: 6 },
    // NFR-07: filled badges and buttons pick dark or light text by the
    // background luminance, so every colour keeps WCAG AA contrast.
    autoContrast: true,
    luminanceThreshold: 0.2,
    variantColorResolver: brandContrast,
  });
}

// The brand colour is a virtual colour (one shade per scheme), which Mantine's
// autoContrast cannot resolve: filled and gradient brand elements take the
// brand pack's own on_primary text colour (checked for contrast at build).
const brandContrast: VariantColorsResolver = (input) => {
  const out = defaultVariantColorsResolver(input);
  const color = input.color ?? input.theme.primaryColor;
  if ((input.variant === "filled" || input.variant === undefined) && color === "brand") {
    return { ...out, color: "var(--mantine-primary-color-contrast)" };
  }
  return out;
};

export function cssVariables(brand: PublicBrand): CSSVariablesResolver {
  const { light, dark } = brand.theme;
  return () => ({
    variables: {},
    light: {
      "--mantine-color-body": light.background,
      "--mantine-color-text": light.text,
      "--mantine-color-dimmed": light.muted,
      "--mantine-color-default": light.surface,
      "--mantine-color-error": light.danger,
      // Red text and outlines (subtle, light and outline variants) use the
      // brand's danger colour, which the build checks for contrast (NFR-07).
      "--mantine-color-red-text": light.danger,
      "--mantine-color-red-light-color": light.danger,
      "--mantine-color-red-outline": light.danger,
      "--mantine-primary-color-contrast": light.on_primary,
      "--brand-surface": light.surface,
    },
    dark: {
      "--mantine-color-body": dark.background,
      "--mantine-color-text": dark.text,
      "--mantine-color-dimmed": dark.muted,
      "--mantine-color-default": dark.surface,
      "--mantine-color-error": dark.danger,
      // Red text and outlines (subtle, light and outline variants) use the
      // brand's danger colour, which the build checks for contrast (NFR-07).
      "--mantine-color-red-text": dark.danger,
      "--mantine-color-red-light-color": dark.danger,
      "--mantine-color-red-outline": dark.danger,
      "--mantine-primary-color-contrast": dark.on_primary,
      "--brand-surface": dark.surface,
    },
  });
}
